import {
  EVButton,
  EVClear,
  EVDialog,
  EVDialogActions,
  EVDialogContent,
  EVDialogContentText,
  EVDialogTitle,
  EVGrid,
  EVInputLabel,
  EVIconButton,
  EVCircularProgress,
  makeStyles,
} from 'lib/components';
import React, { useState, useEffect } from 'lib/react';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import { useDispatch, useSelector } from 'lib/redux';
import SelectField from 'commonComponents/select-field';
import TextInputField from 'commonComponents/textInputField';
import { CANCEL_ORDER_REASON_MAX_LENGTH } from 'constants/AppConstants';
import { OTHER_STATUS_REASON_ID } from '../orderHistory/OrderHistory.constants';
import {
  cancelOrder as cancelOrderAction, closeCancelOrderDialog, setOtherReason, setSelectedReason,
} from './CancelOrder.actions';

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: '478px',
    padding: '24px',
    gap: '24px',
  },
  title: {
    padding: 0,
    '&>h3': {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '18px',
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: '27px',
      textAlign: 'left',
      color: theme.evPalette.charcoal[900],
      textTransform: 'none',
    },
  },
  contentText: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    color: theme.evPalette.charcoal[900],
    gap: '24px',
    '&>span': {
      margin: 0,
    },
  },
  labels: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0px',
    color: theme.evPalette.charcoal[900],
    marginBottom: '8px',
    '&.error, .myev-MuiInputLabel-asterisk': { color: theme.evPalette.rose[900] },
  },
  selectReason: {
    '&.Mui-error > fieldset': { borderColor: `${theme.evPalette.rose[900]} !important` },
  },
  selectError: { color: `${theme.evPalette.rose[900]} !important` },
  actions: {
    '&>button': {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '21.79px',
      textTransform: 'none',
      '&#dialog-yes-cancel-btn': { color: theme.evPalette.raspberry[600] },
    },
  },
}));

/** Component that renders dialog box for cofirming order closure */
function CancelOrderDialog() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectError, setSelectError] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const {
    closureReasons, cancelReportID, selectedReason, otherReason, cancelOrderLoading,
  } = useSelector(({ cancelOrder }) => cancelOrder);

  useEffect(() => {
    if (otherReason.length <= CANCEL_ORDER_REASON_MAX_LENGTH) setCancelReasonError('');
  }, [otherReason]);

  const handleReasonChange = ({ target: { value } }) => {
    dispatch(setSelectedReason(value));
    setSelectError('');
  };
  const handleOtherReasonChange = ({ target: { value } }) => {
    if (value.length > CANCEL_ORDER_REASON_MAX_LENGTH) { setCancelReasonError(t('maximum_length_exceeded')); }
    dispatch(setOtherReason(value));
  };

  const handleClose = () => !cancelOrderLoading && dispatch(closeCancelOrderDialog());

  const handleOrderCancel = () => {
    if (!selectedReason) setSelectError(t('order_history__actions__select_reason_error'));
    else if (!cancelReasonError) {
      dispatch(cancelOrderAction({
        reportId: cancelReportID,
        closureReasonId: selectedReason,
        closureReason: closureReasons.find(({ id }) => id === selectedReason).reason,
        otherReason,
      }));
    }
  };

  return (
    <EVDialog
      open={Boolean(cancelReportID)}
      onClose={handleClose}
      classes={{ paper: classes.dialog }}
    >
      <EVDialogTitle disableTypography className={classes.title}>
        {t('order_history__actions__cancel_order')}
        <EVIconButton onClick={handleClose} id="dialog-close-btn" style={{ padding: 0 }}>
          <EVClear htmlColor="#1B202C" />
        </EVIconButton>
      </EVDialogTitle>
      <EVDialogContent className={classes.contentText}>
        <EVDialogContentText className={classes.contentText}>
          <span>{t('order_history__actions__cancel_order_dialog_1')}</span>
          <span>{t('order_history__actions__cancel_order_dialog_2')}</span>
        </EVDialogContentText>
        <EVGrid>
          <EVInputLabel htmlFor="select-closure-reason" required className={clsx(classes.labels, selectError && 'error')}>
            {t('order_history__actions__select_reason')}
          </EVInputLabel>
          <SelectField
            selectId="select-closure-reason"
            selectedValue={selectedReason}
            selectOptions={closureReasons}
            handleChange={handleReasonChange}
            descriptionKey="reason"
            variant="outlined"
            fullWidth
            errorMessage={selectError}
            errorProps={{ className: classes.selectError }}
            classes={{ root: classes.selectReason }}
          />
        </EVGrid>
        {selectedReason === OTHER_STATUS_REASON_ID && (
          <EVGrid>
            <TextInputField
              inputId="input-other-reason"
              labelText={t('order_history__actions__reason')}
              inputLabelProps={{ className: classes.labels }}
              inputValue={otherReason}
              handleChange={handleOtherReasonChange}
              variant="outlined"
              fullWidth
              error={cancelReasonError}
              helperText={cancelReasonError}
            />
          </EVGrid>
        )}
      </EVDialogContent>
      <EVDialogActions className={classes.actions}>
        <EVButton onClick={handleClose} id="dialog-no-continue-btn" variant="text" color="primary">
          {t('order_history__actions__no_continue')}
        </EVButton>
        <EVButton onClick={handleOrderCancel} id="dialog-yes-cancel-btn" variant="text" className="yesBtn" disabled={cancelOrderLoading}>
          {
            cancelOrderLoading
              ? <EVCircularProgress color="inherit" style={{ width: '24px', height: '24px' }} />
              : t('order_history__actions__yes_cancel')
          }
        </EVButton>
      </EVDialogActions>
    </EVDialog>
  );
}

export default CancelOrderDialog;
